import React, { useState } from "react";
import { CalendarDays, ArrowUpRight, ExternalLink, MapPin } from "lucide-react";
import { tournamentBodies, upcomingTournaments, externalLinks } from "../mock";

const filters = ["All", ...tournamentBodies.map((t) => t.abbr)];

export default function TournamentCalendars() {
  const [active, setActive] = useState("All");

  const events =
    active === "All"
      ? upcomingTournaments
      : upcomingTournaments.filter((e) => e.org.includes(active));

  return (
    <main className="bop-vignette min-h-[70vh]">
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
        <p className="eyebrow">Stay in the game</p>
        <h1 className="mt-4 font-display text-4xl font-extrabold text-white sm:text-5xl">Tournament Calendars</h1>
        <p className="mt-5 max-w-2xl text-lg text-zinc-400">
          CDC, NDFC, WODA, EODA, WDF, PDC, Vault &amp; ADC — every board worth watching, in one place.
          Tap any organisation to open its official schedule, or browse the upcoming 2026 season below.
        </p>

        {/* Org cards -> official calendars */}
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {tournamentBodies.map((t) => (
            <a
              key={t.abbr}
              href={t.url}
              target="_blank"
              rel="noopener noreferrer"
              className="bop-card group rounded-2xl p-6"
            >
              <div className="flex items-center justify-between">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#ff2e4d]/15 text-[#ff2e4d]">
                  <CalendarDays className="h-5 w-5" />
                </span>
                <ArrowUpRight className="h-5 w-5 text-zinc-500 transition-colors group-hover:text-[#ff2e4d]" />
              </div>
              <h3 className="mt-5 font-display text-xl font-bold text-white">{t.abbr}</h3>
              <p className="mt-1 text-sm text-zinc-400">{t.name}</p>
              <p className="mt-3 inline-flex items-center gap-1.5 text-xs text-zinc-500">
                <MapPin className="h-3.5 w-3.5 text-[#ff2e4d]" /> {t.region}
              </p>
            </a>
          ))}
        </div>

        {/* Upcoming schedule */}
        <div className="mt-16">
          <h2 className="font-display text-2xl font-bold text-white">Upcoming 2026 tournaments</h2>
          <p className="mt-2 text-sm text-zinc-400">
            A rolling look at the ranked events across our associations. Filter by organisation.
          </p>

          <div className="mt-6 flex flex-wrap gap-2">
            {filters.map((f) => (
              <button
                key={f}
                onClick={() => setActive(f)}
                className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
                  active === f
                    ? "bg-[#ff2e4d] text-white"
                    : "border border-white/15 text-zinc-300 hover:bg-white/10"
                }`}
              >
                {f}
              </button>
            ))}
          </div>

          <div className="mt-8 overflow-hidden rounded-2xl border border-white/10">
            {events.map((e, i) => {
              const primary = tournamentBodies.find((b) => e.org.includes(b.abbr));
              return (
                <a
                  key={i}
                  href={primary ? primary.url : "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`group flex flex-col gap-2 px-5 py-4 transition-colors hover:bg-white/[0.04] sm:flex-row sm:items-center sm:gap-6 ${
                    i !== events.length - 1 ? "border-b border-white/10" : ""
                  }`}
                >
                  <div className="w-40 flex-none font-display text-sm font-semibold text-[#ff2e4d]">{e.date}</div>
                  <div className="flex-1">
                    <div className="font-medium text-white">{e.event}</div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-zinc-400">
                      <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-zinc-500" /> {e.location}</span>
                      <span className="text-zinc-600">•</span>
                      <span>{e.org}</span>
                    </div>
                  </div>
                  <ArrowUpRight className="hidden h-5 w-5 flex-none text-zinc-600 transition-colors group-hover:text-[#ff2e4d] sm:block" />
                </a>
              );
            })}
            {events.length === 0 && (
              <div className="px-5 py-8 text-center text-sm text-zinc-500">No listed events for {active} yet — check the official calendar above.</div>
            )}
          </div>
          <p className="mt-4 text-xs text-zinc-500">
            Dates compiled from the associations' public calendars and may change — always confirm on the official schedule.
          </p>
        </div>

        {/* Community CTA */}
        <div className="mt-14 flex flex-wrap items-center gap-4 rounded-2xl border border-white/10 bg-[#0f0f11] p-6">
          <p className="text-sm text-zinc-400">
            Looking for match nights and live scores? Jump into the community.
          </p>
          <a
            href={externalLinks.discord}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-lg bg-[#ff2e4d] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#e01f3c] transition-colors"
          >
            Join Community Chat <ExternalLink className="h-4 w-4" />
          </a>
        </div>
      </section>
    </main>
  );
}
